import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AppService } from 'src/app/app.service';
import { MessageEditComponent } from 'src/app/messages/message-edit/message-edit.component';
import { SiteModel } from 'src/app/models/site.model';
import { ApiRequestService } from 'src/app/shared/api-request.service';
import { UtilsService } from 'src/app/shared/utils.service';
import { RBACService } from '../../shared/rbac.service';

@Component({
  selector: 'app-sites-layout',
  templateUrl: './sites-layout.component.html',
  styleUrls: ['./sites-layout.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class SitesLayoutComponent implements OnInit {
  parent_id: number;
  child_id: number;
  site_id: number;

  // site reference
  site: SiteModel = new SiteModel();

  /**
   * Site side navigation visibility flags, each combining the subscription plan gate with the RBAC
   * checks for that menu entry.
   *
   * Resolved once in ngOnInit instead of inline in the template. The site navigation is rendered for
   * the whole time a site is open, so inline checks re-ran on every change detection cycle. Switching
   * accounts re-navigates and therefore recreates this component, so a single resolve is sufficient.
   */
  navCan = {
    sendMessage: false,
    details: false,
    noticeboard: false,
    contractors: false,
    managers: false,
    usersWorkedOn: false,
    mainSafetyPlan: false,
    forms: false,
    qrCodes: false,
    documents: false,
    assets: false,
    hazards: false,
    hazardousSubstances: false,
    safetyObservations: false,
    incidents: false,
    toolboxTalks: false,
    inspectionsAudits: false,
    taskAnalyses: false,
    taJsaSwms: false,
    emergencyEvacuationPlans: false,
    notifications: false
  };

  constructor(
    public route: ActivatedRoute,
    public app: AppService,
    private api: ApiRequestService,
    public router: Router,
    public utils: UtilsService,
    public rbac: RBACService
  ) {}

  ngOnInit(): void {
    if (
      !this.rbac.userCanAccess([
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES,
          RBACService.RBAC_A.A_CREATE
        ),
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES,
          RBACService.RBAC_A.A_VIEW
        )
      ])
    ) {
      this.rbac.showGenericAccessDeniedMessage();
      this.router.navigate(['/sites']);
      return;
    }

    // Resolve the side navigation permission flags.
    this.resolveNavPermissions();

    // Get the site id from the route params.
    this.parent_id = Number(this.route.parent.snapshot.params['parent_id']);
    this.child_id = Number(this.route.parent.snapshot.params['child_id']);

    // Check if we are updating a site or creating a new one.
    if (
      this.child_id ||
      (typeof this.route.parent.snapshot.params['child_id'] == 'undefined' &&
        this.parent_id)
    ) {
      // Store the site id.
      this.site_id = this.child_id ? this.child_id : this.parent_id;

      // get the site
      this.api
        .makeRequest('get', 'v1/sites/' + this.site_id)
        .then(
          (response: any): void => {
            this.site.apply(response.data);
          }
        )
        .catch(
          (err: any): void => {
            this.utils.handleAPIErrors(err.error);
            // Redirect the user back to where they came from.
            this.utils.goBack();
          }
        );
    }
  }

  /**
   * Resolves the site side navigation visibility flags from the subscription plan and the user's RBAC
   * permissions. Mirrors the previous inline template checks exactly.
   *
   * @return {void}
   */
  private resolveNavPermissions(): void {
    const F = RBACService.RBAC_F;
    const A = RBACService.RBAC_A;
    const FEATURES = AppService.FEATURES;

    // sites:view is the base requirement for most site level entries.
    const canViewSiteExact: boolean = this.rbac.userCanAccess(
      [RBACService.getPermission(F.F_SITES, A.A_VIEW)],
      true
    );
    const canViewSiteAny: boolean = this.rbac.userCanAccess([
      RBACService.getPermission(F.F_SITES, A.A_VIEW)
    ]);

    this.navCan = {
      sendMessage:
        this.app.planCan(FEATURES.MESSAGES) &&
        this.rbac.userCanAccess([
          RBACService.getPermission(F.F_MESSAGES, A.A_CREATE)
        ]),
      details: canViewSiteExact,
      noticeboard: canViewSiteAny,
      contractors:
        this.app.planCan(FEATURES.SAFETY_PLANS) &&
        (this.rbac.userCanAccess(
          [
            RBACService.getPermission(F.F_SITES, A.A_VIEW),
            RBACService.getPermission(F.F_CONTRACTORS, A.A_VIEW)
          ],
          true
        ) ||
          this.rbac.userCanAccess([
            RBACService.getPermission(F.F_SITES_SAFETY_PLANS, A.A_CREATE),
            RBACService.getPermission(F.F_SITES_SAFETY_PLANS, A.A_EDIT),
            RBACService.getPermission(F.F_SITES_SAFETY_PLANS, A.A_DELETE)
          ])),
      managers: this.rbac.userCanAccess(
        [
          RBACService.getPermission(F.F_SITES, A.A_VIEW),
          RBACService.getPermission(F.F_SITES_MANAGERS, A.A_VIEW)
        ],
        true
      ),
      usersWorkedOn:
        canViewSiteExact &&
        this.rbac.userCanAccess([
          RBACService.getPermission(F.F_SITES_USERS_WORKED_ON, A.A_VIEW),
          RBACService.getPermission(F.F_SITES_USERS_WORKED_ON, A.A_EDIT),
          RBACService.getPermission(F.F_SITES_USERS_WORKED_ON, A.A_DELETE)
        ]),
      mainSafetyPlan:
        this.app.planCan(FEATURES.SAFETY_PLANS) &&
        canViewSiteAny &&
        this.rbac.userCanAccess([
          RBACService.getPermission(F.F_SITES_SAFETY_PLANS, A.A_CREATE),
          RBACService.getPermission(F.F_SITES_SAFETY_PLANS, A.A_EDIT)
        ]),
      forms:
        canViewSiteAny &&
        this.rbac.userCanAccess([
          RBACService.getPermission(F.F_FORMS, A.A_VIEW),
          RBACService.getPermission(F.F_FORMS, A.A_CREATE),
          RBACService.getPermission(F.F_FORMS, A.A_EDIT),
          RBACService.getPermission(F.F_FORMS, A.A_DELETE)
        ]) &&
        this.app.planCan(FEATURES.FORMS),
      qrCodes:
        canViewSiteAny &&
        this.rbac.userCanAccess([
          RBACService.getPermission(F.F_SITES_QR_CODES, A.A_VIEW),
          RBACService.getPermission(F.F_SITES_QR_CODES, A.A_CREATE)
        ]) &&
        this.app.planCan(FEATURES.QR_CODES),
      documents:
        canViewSiteExact &&
        this.rbac.userCanAccess([
          RBACService.getPermission(F.F_SITES_DOCUMENTS, A.A_VIEW),
          RBACService.getPermission(F.F_SITES_DOCUMENTS, A.A_CREATE)
        ]) &&
        this.app.planCan(FEATURES.DOCUMENTS),
      assets:
        canViewSiteExact &&
        this.rbac.userCanAccess([
          RBACService.getPermission(F.F_SITES_ASSETS, A.A_VIEW),
          RBACService.getPermission(F.F_SITES_ASSETS, A.A_CREATE),
          RBACService.getPermission(F.F_SITES_ASSETS, A.A_EDIT),
          RBACService.getPermission(F.F_SITES_ASSETS, A.A_DELETE)
        ]) &&
        this.app.planCan(FEATURES.ASSETS),
      hazards:
        canViewSiteExact &&
        this.rbac.userCanAccess([
          RBACService.getPermission(F.F_SITES_HAZARDS_RISKS, A.A_VIEW),
          RBACService.getPermission(F.F_SITES_HAZARDS_RISKS, A.A_CREATE)
        ]) &&
        this.app.planCan(FEATURES.HAZARDS),
      hazardousSubstances:
        this.rbac.userCanAccess([
          RBACService.getPermission(
            F.F_REGISTERS_HAZARDOUS_SUBSTANCES,
            A.A_CREATE
          ),
          RBACService.getPermission(F.F_SITES_HAZARDOUS_SUBSTANCES, A.A_VIEW)
        ]) && this.app.planCan(FEATURES.HAZARDOUS_SUBSTANCES),
      safetyObservations:
        this.rbac.userCanAccess([
          RBACService.getPermission(F.F_SITES_SAFETY_OBSERVATIONS, A.A_CREATE),
          RBACService.getPermission(F.F_SITES_SAFETY_OBSERVATIONS, A.A_VIEW),
          RBACService.getPermission(F.F_SITES_SAFETY_OBSERVATIONS, A.A_EDIT),
          RBACService.getPermission(F.F_SITES_SAFETY_OBSERVATIONS, A.A_DELETE)
        ]) && this.app.planCan(FEATURES.SAFETY_OBSERVATIONS),
      incidents:
        this.app.planCan(FEATURES.INCIDENTS) &&
        this.rbac.userCanAccess([
          RBACService.getPermission(F.F_SITES_INCIDENTS, A.A_VIEW),
          RBACService.getPermission(F.F_SITES_INCIDENTS, A.A_CREATE),
          RBACService.getPermission(F.F_SITES_INCIDENTS, A.A_EDIT),
          RBACService.getPermission(F.F_SITES_INCIDENTS, A.A_DELETE)
        ]),
      toolboxTalks:
        this.rbac.userCanAccess([
          RBACService.getPermission(F.F_SITES_TOOLBOX_TALKS, A.A_VIEW),
          RBACService.getPermission(F.F_SITES_TOOLBOX_TALKS, A.A_CREATE),
          RBACService.getPermission(F.F_SITES_TOOLBOX_TALKS, A.A_EDIT),
          RBACService.getPermission(F.F_SITES_TOOLBOX_TALKS, A.A_DELETE)
        ]) && this.app.planCan(FEATURES.TOOLBOX_TALKS),
      inspectionsAudits:
        canViewSiteExact &&
        this.rbac.userCanAccess([
          RBACService.getPermission(F.F_SITES_INSPECTIONS_AUDITS, A.A_VIEW),
          RBACService.getPermission(F.F_SITES_INSPECTIONS_AUDITS, A.A_EDIT),
          RBACService.getPermission(F.F_SITES_INSPECTIONS_AUDITS, A.A_DELETE)
        ]) &&
        this.app.planCan(FEATURES.INSPECTIONS_AUDITS),
      taskAnalyses:
        this.rbac.userCanAccess([
          RBACService.getPermission(
            F.F_SITES_TASK_ANALYSIS_DOCUMENTS,
            A.A_VIEW
          ),
          RBACService.getPermission(
            F.F_SITES_TASK_ANALYSIS_DOCUMENTS,
            A.A_CREATE
          ),
          RBACService.getPermission(
            F.F_SITES_TASK_ANALYSIS_DOCUMENTS,
            A.A_EDIT
          ),
          RBACService.getPermission(
            F.F_SITES_TASK_ANALYSIS_DOCUMENTS,
            A.A_DELETE
          )
        ]) && this.app.planCan(FEATURES.TASK_ANALYSIS),
      taJsaSwms:
        canViewSiteExact &&
        this.rbac.userCanAccess([
          RBACService.getPermission(F.F_SITES_TA_JSA_SWMS, A.A_VIEW),
          RBACService.getPermission(F.F_SITES_TA_JSA_SWMS, A.A_CREATE),
          RBACService.getPermission(F.F_SITES_TA_JSA_SWMS, A.A_EDIT),
          RBACService.getPermission(F.F_SITES_TA_JSA_SWMS, A.A_DELETE)
        ]) &&
        this.app.planCan(FEATURES.TASK_ANALYSIS),
      emergencyEvacuationPlans:
        canViewSiteAny &&
        this.rbac.userCanAccess([
          RBACService.getPermission(F.F_SITES_EEP, A.A_VIEW),
          RBACService.getPermission(F.F_SITES_EEP, A.A_EDIT)
        ]) &&
        this.app.planCan(FEATURES.EMERGENCY_EVACUATION_PLAN),
      notifications:
        this.app.planCan(FEATURES.NOTIFICATION_ROLES) &&
        this.rbac.userCanAccess(
          [RBACService.getPermission(F.F_ACCOUNT_NOTIFICATION_ROLES, A.A_VIEW)],
          true
        )
    };
  }

  /**
   * Composes a new message for the specified site or the currently opened site.
   *
   * @param {number} [siteId=null] - The identifier of the site for which the message is being created. If not provided, the currently opened site will be used.
   * @return {void} Does not return any value.
   */
  composeNewMessage(siteId: number = null): void {
    if (
      !this.rbac.userCanAccess([
        RBACService.getPermission(
          RBACService.RBAC_F.F_MESSAGES,
          RBACService.RBAC_A.A_CREATE
        )
      ])
    ) {
      this.rbac.showGenericAccessDeniedMessage();
      return;
    }

    if (!siteId && !this.site.id) {
      this.utils.showToast('You need to open a site first.');
      return;
    }

    this.utils
      .showComponentDialog(
        MessageEditComponent,
        {
          preSelectSiteIds: [siteId]
        },
        {
          width: '1200px'
        }
      )
      .then(
        (response: any): void => {
          if (response) {
            this.utils.showToast(
              'You can view the message stats in the messages section.'
            );
          }
        }
      );
  }

  protected readonly RBACService = RBACService;
  protected readonly AppService = AppService;
}
